import { io } from "socket.io-client";
import { readCurrentUser } from "./currentUser";
import { decodeJwtPayload, isTokenExpired } from "./tokenExpiry";
import { SESSION_REFRESHED_EVENT, SESSION_REFRESH_REQUESTED_EVENT } from "./sessionEvents";

// #87 (ARCH-032) / SEC-085: the one socket.io connection for the whole app.
//
// It used to connect at import time with no token and was never closed on
// logout, so the socket outlived the session and a second user in the same
// tab inherited it. Now:
//
// - `autoConnect: false`: importing this module (five components do) opens
//   nothing. AuthContext calls connectSocket() once the session verifies.
// - `auth` is a callback, so every handshake (the first connect and every
//   reconnect) reads the CURRENT access token from storage. The token
//   rides in the handshake payload, never the URL.
// - The server (backend BE-1) refuses a bad token with connect_error
//   'unauthorized' and ends the connection at token expiry with
//   'io server disconnect'. socket.io does not retry either on its own, so
//   both ask sessionRefresh.js for its single-flight refresh (via a window
//   event; see sessionEvents.js) and reconnect when it announces success.
//   A failed refresh logs out, and logout calls disconnectSocket().
// - Every logout path ends in clearSession() → disconnectSocket().
//
// Against today's backend, which has no handshake middleware, the token is
// ignored and none of the recovery paths fire, so this is safe to ship first.

const currentToken = () => readCurrentUser()?.token || null;

// The account a token belongs to. A value that is not a decodable JWT falls
// back to the raw string, so two different opaque tokens still count as two
// identities rather than as "the same unknown user".
const identityOf = (token) => (token ? decodeJwtPayload(token)?.userId ?? token : null);

const socket = io(process.env.REACT_APP_BACKEND, {
  autoConnect: false,
  withCredentials: true,
  auth: (cb) => cb({ token: currentToken() }),
});

// True only between connectSocket() and disconnectSocket(), so a background
// refresh or a server disconnect never resurrects a socket nobody asked for.
let wantConnected = false;
// The account the current connection was opened for.
let connectedIdentity = null;
// One refresh request per outage; cleared by a successful connect.
let refreshRequested = false;

function requestSessionRefresh() {
  if (refreshRequested) return;
  refreshRequested = true;
  window.dispatchEvent(new CustomEvent(SESSION_REFRESH_REQUESTED_EVENT));
}

export function connectSocket() {
  const token = currentToken();
  if (!token) return;
  const identity = identityOf(token);
  wantConnected = true;
  if (socket.active && identity === connectedIdentity) return;
  if (socket.active) socket.disconnect();
  // A fresh (re)connect starts clean. Emits made after a logout (say, a
  // message sent when an upload finishes) re-queue into sendBuffer, and
  // socket.io would flush them into this session, whoever signs in. Only
  // connectSocket() clears: the reconnect paths below keep their buffer.
  socket.sendBuffer = [];
  connectedIdentity = identity;
  refreshRequested = false;
  socket.connect();
}

export function disconnectSocket() {
  wantConnected = false;
  connectedIdentity = null;
  refreshRequested = false;
  // socket.io flushes emits made while disconnected on the next connect;
  // after a logout that would be the next user's connection.
  socket.sendBuffer = [];
  socket.disconnect();
}

// Pooled inbox: the socket carries no write events (sends go over HTTP,
// thread.services.js); it only joins and leaves thread rooms. The server
// forgets rooms on reconnect, so ThreadPage and InboxPage re-join from their
// own "connect" listeners and refetch what they missed.
//
// `socket.emit(event, payload, ack)` on a disconnected socket buffers the
// packet and parks the ack until a connect that may never come, hanging the
// caller. `.timeout(ms)` is socket.io-client's ack-timeout decorator: it
// changes the callback signature to `(timeoutErr, ack)`, with the timeout
// Error (if any) FIRST.
const JOIN_THREAD_TIMEOUT_MS = 5000;

export const joinThread = (threadId) =>
  new Promise((resolve) =>
    socket
      .timeout(JOIN_THREAD_TIMEOUT_MS)
      .emit("join thread", { threadId }, (timeoutErr, ack) =>
        resolve(timeoutErr ? { code: "timeout" } : (ack ?? null)),
      ),
  );

export const leaveThread = (threadId) =>
  socket.emit("leave thread", { threadId });

function resumeIfWanted() {
  if (!wantConnected) return;
  const token = currentToken();
  if (!token || identityOf(token) !== connectedIdentity) {
    // The session ended or changed hands; AuthContext connects the new one.
    disconnectSocket();
    return;
  }
  if (!socket.active) socket.connect();
}

socket.on("connect", () => {
  refreshRequested = false;
});

socket.on("connect_error", (err) => {
  if (!wantConnected || err?.message !== "unauthorized") return;
  requestSessionRefresh();
});

socket.on("disconnect", (reason) => {
  if (!wantConnected || reason !== "io server disconnect") return;
  if (isTokenExpired(currentToken())) {
    requestSessionRefresh();
  } else {
    socket.connect();
  }
});

if (typeof window !== "undefined") {
  window.addEventListener(SESSION_REFRESHED_EVENT, resumeIfWanted);
  // Another tab logged out or signed in as someone else: this tab's socket
  // must not stay authenticated as an account that is no longer stored.
  window.addEventListener("storage", (event) => {
    if (event.key !== null && event.key !== "currentUser") return;
    if (wantConnected && identityOf(currentToken()) !== connectedIdentity) {
      disconnectSocket();
    }
  });
}

export default socket;
